/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import React, {
    ForwardRefRenderFunction,
    Ref,
    RefObject,
    forwardRef,
    useCallback,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import SmallArrowIcon from '@toucan/client/global/icons/legacy/small-arrow.svg';

import { useIsomorphicLayoutEffect } from '@toucan/client/global/nextjs';

import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';
import { useWindowResizeCallback } from '@toucan/client/shared/hooks/use-window-resize';

import { useSelectA11y } from '@toucan/client/features/search/hooks/use-select-a11y';

import { styles } from './custom-select.styles';
import {
    CustomSelectAlignment,
    CustomSelectProps,
    StyledCustomSelectProps,
} from './types';

export const DEFAULT_TEST_ID = 'custom-select';

export const StyledContainer = styled.div`
    position: relative;
`;

export const StyledCustomSelect = styled.button<StyledCustomSelectProps>`
    ${styles}
`;

const CustomSelectWithRef: ForwardRefRenderFunction<
    HTMLElement,
    CustomSelectProps
> = (
    {
        'data-testid': dataTestId = DEFAULT_TEST_ID,
        closeMenuAriaLabel = '',
        openMenuAriaLabel = '',
        isActive,
        isOpen = false,
        id,
        label,
        children,
        onClickOut,
        onToggleOpen,
        ...otherProps
    }: CustomSelectProps,
    ref: Ref<HTMLElement>
) => {
    // Accessibility props for the custom select
    const { getRootProps, getToggleButtonProps, getLabelProps, getMenuProps } =
        useSelectA11y();

    const [alignment, setAlignment] = useState<CustomSelectAlignment>(
        CustomSelectAlignment.LEFT
    );

    // Handle closing the menu when clicking outside the panel
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(
        (ref as RefObject<HTMLButtonElement>)?.current || null
    );

    /**
     * Updates the positioning of the menu
     */
    const updateMenuPositioning = useCallback(() => {
        if (!isOpen) {
            setAlignment(CustomSelectAlignment.LEFT);
            return;
        }

        if (!menuRef.current) {
            return;
        }

        // Add class to apply default styles instantly
        menuRef.current.classList.add('open-left');

        const { left, width } = menuRef.current.getBoundingClientRect();
        if (left + width > window.outerWidth) {
            setAlignment(CustomSelectAlignment.RIGHT);
        } else {
            setAlignment(CustomSelectAlignment.LEFT);
        }

        menuRef.current.classList.remove('open-left');
    }, [isOpen]);

    useClickOutside([menuRef, buttonRef], () => {
        if (isOpen && onClickOut) {
            onClickOut();
        }
    });

    useKeyPress(
        buttonRef.current,
        ['ArrowDown', 'Down', 'ArrowUp', 'Up'],
        (event: any) => {
            event.preventDefault();
            if (isOpen) {
                return;
            }
            onToggleOpen && onToggleOpen();
        }
    );

    useWindowResizeCallback(updateMenuPositioning);
    useIsomorphicLayoutEffect(updateMenuPositioning, [isOpen]);

    return (
        <StyledContainer
            data-testid={dataTestId}
            {...otherProps}
            {...getRootProps()}
        >
            <StyledCustomSelect
                isActive={isActive}
                isOpen={isOpen}
                alignment={alignment}
                onClick={onToggleOpen}
                {...getToggleButtonProps(
                    isOpen,
                    closeMenuAriaLabel,
                    openMenuAriaLabel
                )}
                {...getLabelProps(id)}
                ref={buttonRef}
            >
                {label}
                <SmallArrowIcon />
            </StyledCustomSelect>
            {isOpen && (
                <div
                    className="options"
                    {...getMenuProps(id)}
                    ref={menuRef}
                    data-testid={`${dataTestId}__options`}
                    data-alignment={alignment}
                >
                    {children}
                </div>
            )}
        </StyledContainer>
    );
};

export const CustomSelect = forwardRef(CustomSelectWithRef);
